/**
 * ListWidgetsBar — pasek kart podsumowania (widgetów) nad listą. Runtime
 * zakładki „Widgety" Kreatora Listy (Sesja 4, I1).
 *
 * Pobiera wartości z `GET /api/system-modules/:slug/stats` i renderuje siatkę
 * kart wg definicji `widgets` z `list_config`. Każda karta: etykieta + wartość
 * (licznik / agregat) + akcent koloru. Klik karty z `clickAction === 'filter'`
 * woła `onWidgetClick(widget)` — callsite (lista) zawęża widok do filtra widgetu.
 *
 * I1 renderuje counter + aggregate (liczby). Wykresy (pie/bar/trend…) dojdą
 * w kolejnych iteracjach — typy spoza I1 backend zwraca z `value: null`, karta
 * pokazuje „—".
 *
 * Wygląd karty: `widget.style ?? widgetStyle` — pojedyncza karta może mieć
 * własny styl (edytowany w modalu widgetu) nadpisujący domyślny styl pulpitu
 * (`pageConfig.widgetStyle`, sekcja „Domyślny wygląd kart" w zakładce „Widgety").
 *
 * Samowystarczalny: nie zależy od wewnętrznego stanu tabeli. Montowany przez
 * `ConfigurableTable` (gdy `pageConfig.showStats`) między paskiem akcji a tabelą.
 */

import { useTranslation } from 'react-i18next';
import type { RoundingScale, ShadowScale, WidgetConfig, WidgetStyle } from '@saas/shared';
import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';

/** Skala zaokrąglenia → px (mirror z ListPagePanel — wspólny kontrakt wyglądu). */
const ROUNDING_PX: Record<RoundingScale, number> = {
  none: 0,
  sm: 4,
  md: 6,
  lg: 8,
  xl: 12,
  '2xl': 16,
};

/** Skala cienia → CSS box-shadow (mirror z ListPagePanel). */
const SHADOW_CSS: Record<ShadowScale, string> = {
  none: 'none',
  sm: '0 1px 2px 0 rgba(0,0,0,0.06)',
  md: '0 4px 6px -1px rgba(0,0,0,0.12)',
  lg: '0 10px 15px -3px rgba(0,0,0,0.14)',
  xl: '0 20px 25px -5px rgba(0,0,0,0.16)',
};

/**
 * Buduje inline-styl karty widgetu z efektywnego stylu (`widget.style ?? widgetStyle`).
 * Gradient ma pierwszeństwo nad kolorem tła; efekt szkła = półprzezroczyste tło
 * + blur. Zwraca pusty obiekt gdy styl nie ustawiony — zostają klasy motywu.
 */
function widgetCardStyleFrom(style: WidgetStyle | undefined): React.CSSProperties {
  if (!style) return {};
  return {
    background: style.backgroundGradient
      ? `linear-gradient(135deg, ${style.backgroundGradient.from}, ${style.backgroundGradient.to})`
      : (style.backgroundColor ?? (style.glassEffect ? 'rgba(255,255,255,0.08)' : undefined)),
    color: style.textColor,
    borderRadius: style.rounding != null ? ROUNDING_PX[style.rounding] : undefined,
    boxShadow: style.shadow != null ? SHADOW_CSS[style.shadow] : undefined,
    backdropFilter: style.glassEffect ? 'blur(8px)' : undefined,
    WebkitBackdropFilter: style.glassEffect ? 'blur(8px)' : undefined,
  };
}

interface StatsResponse {
  widgets: Array<{ id: string; type: string; value: number | null }>;
  slug: string;
  supported: boolean;
}

interface ListWidgetsBarProps {
  /** Slug modułu systemowego (np. `contractors`). */
  slug: string;
  /** Definicje widgetów z aktywnego layoutu listy (`list_config.widgets`). */
  widgets: WidgetConfig[];
  /** Opcjonalny UUID kategorii rekordu (kaskada wyboru layoutu). */
  categoryId?: string | null;
  /**
   * Firma listy — DOKŁADNIE ta wartość `?companyId=`, którą strona wysyła do swojej listy
   * (`companyIdParam`): przy wybranej firmie jej id, w trybie „Wszystkie firmy" brak. Widgety
   * liczą wtedy te same rekordy, które pokazuje lista. Strona, której lista nie zawęża po firmie
   * (np. Firmy), nie podaje nic.
   */
  companyId?: string;
  /** Klik karty z `clickAction === 'filter'`. */
  onWidgetClick?: (widget: WidgetConfig) => void;
  /** Domyślny wygląd kart (zakładka „Widgety" Kreatora). Brak = wygląd motywu. */
  widgetStyle?: WidgetStyle;
  /**
   * Podgląd „na żywo" Kreatora: gotowe wartości `widget.id → wartość` liczone
   * z syntetycznych wierszy (`lib/preview/widget-preview-values`). Gdy podane,
   * pasek NIE woła `GET …/stats` (w scope admina nie ma danych tenanta).
   */
  staticValues?: Record<string, number | null>;
}

const numberFmt = new Intl.NumberFormat('pl-PL', { maximumFractionDigits: 2 });

/** Szerokość karty w kolumnach siatki (size 1–4). Domyślnie 1. */
function spanClass(size: number | undefined): string {
  switch (size) {
    case 4:
      return 'col-span-2 sm:col-span-4';
    case 3:
      return 'col-span-2 sm:col-span-3';
    case 2:
      return 'col-span-2';
    default:
      return 'col-span-1';
  }
}

export function ListWidgetsBar({
  slug,
  widgets,
  categoryId,
  companyId,
  onWidgetClick,
  widgetStyle,
  staticValues,
}: ListWidgetsBarProps): React.ReactElement | null {
  const { t } = useTranslation();

  const sorted = [...widgets].sort((a, b) => a.order - b.order);

  // Routing endpointu po prefiksie (jak w useTableLayout):
  //  - `module-<id>` → moduł własny: `/modules/:id/stats`
  //  - inny slug     → moduł systemowy: `/system-modules/:slug/stats`
  const statsPath = slug.startsWith('module-')
    ? `/modules/${slug.replace(/^module-/, '')}/stats`
    : `/system-modules/${slug}/stats`;

  const { data, isLoading } = useQuery({
    queryKey: ['list-widgets-stats', slug, categoryId ?? null, companyId ?? null],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (categoryId) params.set('categoryId', categoryId);
      if (companyId) params.set('companyId', companyId);
      const query = params.toString();
      const res = await api.get<StatsResponse>(query ? `${statsPath}?${query}` : statsPath);
      return res.data;
    },
    enabled: widgets.length > 0 && !!slug && !staticValues,
    staleTime: 30 * 1000,
  });

  if (widgets.length === 0) return null;

  const valueById = staticValues
    ? new Map<string, number | null>(Object.entries(staticValues))
    : new Map<string, number | null>((data?.widgets ?? []).map((w) => [w.id, w.value]));

  return (
    <div
      className="grid grid-cols-2 gap-3 sm:grid-cols-4"
      data-testid="list-widgets-bar"
      role="list"
    >
      {sorted.map((widget) => {
        const value = valueById.get(widget.id);
        const clickable = (widget.clickAction ?? 'filter') === 'filter' && !!onWidgetClick;
        const display =
          isLoading && value === undefined ? '…' : value == null ? '—' : numberFmt.format(value);

        // Wygląd per karta: własny `widget.style` nadpisuje domyślny `widgetStyle`.
        const effectiveStyle = widget.style ?? widgetStyle;
        const cardStyle = widgetCardStyleFrom(effectiveStyle);
        const headerColor = effectiveStyle?.headerColor;
        const valueColor = effectiveStyle?.textColor;

        return (
          <button
            key={widget.id}
            type="button"
            role="listitem"
            disabled={!clickable}
            onClick={clickable ? () => onWidgetClick!(widget) : undefined}
            data-testid={`list-widget-card-${widget.id}`}
            title={clickable ? t('tenant.listBuilder.widgets.bar.filterHint') : undefined}
            className={`${spanClass(widget.size)} flex flex-col gap-1 rounded-lg border border-border-secondary bg-bg-elevated p-3 text-left transition-colors ${
              clickable ? 'cursor-pointer hover:border-accent-400' : 'cursor-default'
            }`}
            style={{ ...cardStyle, borderLeftColor: widget.color, borderLeftWidth: 3 }}
          >
            <span
              className="truncate text-xs font-medium text-text-tertiary"
              style={headerColor ? { color: headerColor } : undefined}
            >
              {widget.label}
            </span>
            <span
              className="text-xl font-semibold text-text-primary"
              style={valueColor ? { color: valueColor } : undefined}
            >
              {display}
            </span>
          </button>
        );
      })}
    </div>
  );
}
